import axios from 'crypto-fetch/axios'
import xfetch from '@ms/fetch'
import customPush from 'custom-push'
import { getTopic } from 'custom-push/lib/pushTopic.js'
import { prefillMatchSummary } from '@ms/components/match/useMatchInfo.js'
import { DEFAULT_ERROR_TEXT } from '@ms/config/message.js'
import { pick } from 'lodash-es'
import { isLiveMatch } from '@ms/components/match/status.js'
import router from 'wap/router'
import { getMatchDetailLink } from '@ms/routes/match'
import { useCommentsStore } from 'wap/stores/comments'

export default {
  /**
   * 获取比赛详情信息, 含 market
   * @param  {String} eventId        [description]
   * @return {[type]}                [description]
   */
  async getMatchDetail() {
    const { eventId } = this
    try {
      const data = await axios.get(`/facts-center/query/frontend/match/detail?eventId=${eventId}`)
      const { status } = data
      if (status === 3 || status === 4) {
        return router.replace(getMatchDetailLink(data))
      }
      prefillMatchSummary(data)
      this.setMatchDetail(data)
      return data
    } catch (error) {
      console.error(error)
      return Promise.reject(new Error(DEFAULT_ERROR_TEXT))
    }
  },

  /**
   * 获取market group 列表
   * @param  {String} sportId        [description]
   * @return {[type]}                [description]
   */
  async getMarketGroupList(sportId = '') {
    const data = await axios.get('/facts-center/query/frontend/market-group-info', {
      params: {
        sportId
      }
    })
    this.setMarketGroupList(data.markets)
    return data
  },

  /**
   * 获取用户收藏的markets列表
   * @param  {[type]}  options.sportId      sport id
   * @return {[type]}                       [description]
   */
  async getFavouriteMarkets(sportId = '') {
    const { bizCode, data, message } = await axios.get(`/facts-center/query/frontend/my-favourites/markets?sportId=${sportId}`, { self: true })
    if (bizCode === 10000 && data) {
      this.allFavoriteMarketIds(data)
      return data
    } else {
      return Promise.reject(new Error(message))
    }
  },

  /**
   * 添加/取消收藏 market
   * @param  {[type]}  options.sportId      sport id
   * @param  {[type]}  options.marketId     market id
   * @param  {Boolean} options.status       添加或取消的状态，true 为收藏，false 为取消收藏
   * @return {[type]}                       [description]
   */
  async toggleFavouriteMarket({ sportId = '', marketId = '', status = true }) {
    const method = status ? 'put' : 'delete'
    await axios[method](`/facts-center/query/frontend/my-favourites/markets?sportId=${sportId}&marketId=${marketId}`)
    this.updateFavouriteMarket({ marketId, status })
  },

  async getLiveMatches() {
    const { sportId, eventId } = this.event
    const data = await axios.get(`/facts-center/query/frontend/live-matches/list?sportId=${sportId}&eventId=${eventId}`)
    this.setLiveMatches(data)
    return data
  },

  async getCommentary({ limit = 20 } = {}) {
    const { eventId, event, lastCommentaryId } = this
    const { sportId } = event
    const data = await axios.get(`/gossip/commentary/list?sportId=${sportId}&eventId=${eventId}&lastCommentaryId=${lastCommentaryId}&limit=${limit}`)
    this.liveCommentary(data)
    useCommentsStore().updateCommentsNum(data.commentsNum)
  },

  // 比赛信息订阅回调函数列表，保持引用关系
  // type === 1
  eventPushCallback(data) {
    data = JSON.parse(data)
    this.updateMatch(data)
  },

  // 订阅比赛详情 event 信息
  subEvent({ isSub = true } = {}) {
    const { sportId, categoryId, tournamentId, eventId } = this.event
    if (!sportId) return
    customPush.pushTopic(
      {
        type: 1,
        sportId,
        categoryId,
        tournamentId,
        eventId
      },
      this.eventPushCallback,
      isSub
    )
  },

  // type === 2
  marketPushCallback(data) {
    data = JSON.parse(data)
    this.updateMarket(data)
  },

  // type === 3
  oddsPushCallback(data) {
    data = JSON.parse(data)
    this.updateOutcome(data)
  },

  // 订阅 markets + odds 信息
  subMarkets({ isSub = true, marketId, marketIds, specifiers } = {}) {
    const { sportId, categoryId, tournamentId, eventId } = this.event
    if (!sportId) return
    const marketPushCallbackList = [this.marketPushCallback, this.oddsPushCallback]
    marketPushCallbackList.forEach((callback, index) => {
      const option = {
        type: index + 2,
        sportId,
        categoryId,
        tournamentId,
        eventId,
        specifiers
      }
      if (marketId) {
        option.marketIds = [marketId]
      } else if (marketIds) {
        option.marketIds = marketIds
      }

      if (isSub) {
        this.updateSubedMarketIds(marketId || marketIds)
      }
      customPush.pushTopic(option, callback, isSub)
    })
  },

  // change matches 模块赛事添加订阅回调函数
  liveListCallback(data) {
    data = JSON.parse(data)
    this.updateLiveMatches(data)
  },

  // change matches 模块赛事添加/取消订阅
  subLiveMatches({ isSub = true } = {}) {
    const { liveListCallback } = this
    const { liveMatches } = this
    if (liveMatches.order && liveMatches.order.length > 0) {
      liveMatches.order.forEach((tournamentId) => {
        const { events } = liveMatches.map[tournamentId]
        events.order.forEach((eventId) => {
          const { sportId, categoryId, tournamentId } = events.map[eventId]
          // 只订阅 event 信息
          customPush.pushTopic(
            {
              type: 1,
              sportId,
              categoryId,
              tournamentId,
              eventId
            },
            liveListCallback,
            isSub
          )
        })
      })
    }
  },

  // commentary 信息流推送
  subCommentary({ isSub = true } = {}) {
    const { eventId } = this.event
    const topic = `commentary_topic_${eventId}`
    const method = isSub ? 'sub' : 'unsub'
    customPush[method]({
      topic,
      pushType: 'GROUP',
      listener: isSub
        ? (data) => {
            data = JSON.parse(data)
            if (eventId && eventId !== this.eventId) return
            this.updateCommentaryList(data)
          }
        : null
    })
  },

  // 订阅黑名单（加入黑名单的 market / odds 不再推送）
  subBlacklist({ isBlack = true, isSub = true, marketIds = [], specifiers }) {
    const options = pick(this.event, ['sportId', 'categoryId', 'tournamentId', 'eventId'])
    const marketId = marketIds.join(',')
    const method = isSub ? 'sub' : 'unsub'
    // let marketTopic = getTopic({ ...options, marketId, specifiers }, 2)
    let oddsTopic = getTopic({ ...options, marketId, specifiers }, 3)

    if (isBlack) {
      // marketTopic = `blacklistTopic^${marketTopic}`
      oddsTopic = `blacklistTopic^${oddsTopic}`

      this.subscribedIgpomsTopicSet.delete(oddsTopic)
      this.subscribedIgpomsTopicSet = new Set(this.subscribedIgpomsTopicSet)
    } else {
      this.subscribedIgpomsTopicSet = new Set([...this.subscribedIgpomsTopicSet, oddsTopic])
    }

    // customPush[method]({
    //   topic: marketTopic,
    //   pushType: 'GROUP',
    //   listener: null
    // })
    customPush[method]({
      topic: oddsTopic,
      pushType: 'GROUP',
      listener: function () {
        return null
      }
    })
  },

  async fetchMarketInfo({ marketId = '', specifiers = '' }) {
    const product = isLiveMatch(this.event.status) ? 1 : 3
    const params = pick(this.event, ['sportId', 'eventId'])
    params.product = product
    params.marketId = marketId
    if (specifiers) {
      params.specifiers = specifiers
    }
    const { bizCode, data, message } = await axios.get('/facts-center/query/frontend/market-info', { params, self: true })
    if (bizCode === 10000 && data) {
      if (data.id) {
        this.updateMatchDetail(data)
        this.subBlacklist({ isBlack: false, isSub: true, marketIds: [marketId], specifiers })
      }
    } else {
      return Promise.reject(new Error(message))
    }
  },

  /**
   * 批量获取 market 的详细信息，包括赔率
   * @see https://github.com/WorldSaberTech/documents/blob/main/01%20-%20MSports%20%E5%8D%8F%E4%BD%9C%E6%96%87%E6%A1%A3/02%20-%20%E5%89%8D%E7%AB%AF%E6%8E%A5%E5%8F%A3/Query%E4%BD%93%E8%82%B2%E6%9F%A5%E8%AF%A2%E7%B3%BB%E7%BB%9F/%E5%85%AC%E5%85%B1%E6%8E%A5%E5%8F%A3%20-%20%E6%AF%94%E8%B5%9B%E8%AF%A6%E6%83%85/%E6%89%B9%E9%87%8F%E6%9F%A5%E8%AF%A2%20Market%20%E8%AF%A6%E7%BB%86%E4%BF%A1%E6%81%AF.md
   * @param {{
   *   sportId: string,
   *   eventId: string,
   *   marketId: string,
   *   specifiers?: string,
   *   product: number
   * }[]} body
   * @param {AbortSignal?} abortSignal
   */
  async fetchMarketInfoBatch(body, abortSignal) {
    const { error, data } = await xfetch('/facts-center/query/frontend/market-info/batch', {
      method: 'POST',
      body,
      signal: abortSignal
    })

    if (error) {
      return Promise.reject(error)
    }

    this.updateMatchDetail(data)
    data.forEach((market) => {
      this.subBlacklist({ isBlack: false, isSub: true, marketIds: [market.id], specifiers: market.specifiers })
    })
  },

  async fetchMarketsByIds(marketIds = []) {
    const { eventId } = this.event
    try {
      // 先标记下正在请求的 market id
      this.updateReqMarketIds(marketIds)
      const { bizCode, data } = await axios.post(`/facts-center/query/frontend/market-info/bet-able-by-market-ids?eventId=${eventId}`, marketIds, { self: true })
      if (bizCode === 10000 && data.length) {
        this.setCommentaryMarkets(data)
        const ids = marketIds.filter((id) => !data.find((x) => +x.id === +id))
        this.removeReqMarketId(ids)
        return marketIds.length !== ids.length
      } else {
        this.removeReqMarketId(marketIds)
        return false
      }
    } catch (error) {
      console.error(error)
      // 请求出错不做处理
      return false
    }
  },

  async getCommentaryNewMessage({ lastCommentaryId = '', latestCommentaryId = '' }) {
    const { eventId } = this.event
    const data = await axios.get('/gossip/commentary/new-messages', {
      params: {
        eventId,
        lastCommentaryId,
        latestCommentaryId
      }
    })
    this.liveCommentaryInfo(data)
  }
}
